'use client';

import Image from '@/components/ui/CspImage';
import { Link } from '@/i18n/navigation';
import SiteFooter from '@/components/landing/SiteFooter';
import { useTranslations } from 'next-intl';
import { ArrowLeft } from 'lucide-react';
import LanguageSwitcher from './LanguageSwitcher';

interface StaticPageLayoutProps {
  title: string;
  lastUpdated?: string;
  children: React.ReactNode;
}

export default function StaticPageLayout({ title, lastUpdated, children }: StaticPageLayoutProps) {
  const t = useTranslations();
  return (
    <div className="landing-theme min-h-screen bg-background font-body">
      {/* Header - matches landing page nav */}
      <header className="border-b border-border bg-background/95 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link href="/" className="group flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors">
              <ArrowLeft className="w-4 h-4 transition-transform duration-200 group-hover:-translate-x-1" />
              <span className="text-sm font-medium">{t('staticPage.backToHome')}</span>
            </Link>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <Link href="/" className="flex items-center">
              <Image
                src="/StyTrix_brand.png"
                alt="StyTrix"
                width={140}
                height={37}
                className="block dark:hidden w-auto h-[2.25rem]"
                priority
              />
              <Image
                src="/StyTrix_brand_dark.png"
                alt="StyTrix"
                width={140}
                height={37}
                className="hidden dark:block w-auto h-[2.25rem]"
                priority
              />
            </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-16 pb-12 px-6 bg-gradient-to-b from-background via-secondary/30 to-background grain-overlay">
        <div className="max-w-4xl mx-auto">
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4 tracking-tight">{title}</h1>
          {lastUpdated && (
            <p className="text-sm text-muted-foreground">{t('staticPage.lastUpdated', { date: lastUpdated })}</p>
          )}
        </div>
      </section>

      {/* Content */}
      <main id="main-content" className="max-w-4xl mx-auto px-6 py-16">
        {children}
      </main>

      <SiteFooter />
    </div>
  );
}
